import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, StyleSheet, View } from 'react-native'
import { FirmwareState, useStore } from 'zens-store'
import { ProgressBar, Spacing, Text } from 'zens-storybook'
import { observer } from 'mobx-react-lite'
import { useParams } from 'zens-router'
import { formatDuration } from 'date-fns'
import { useKeepAwake } from 'expo-keep-awake'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: Spacing.M,
  },
  text: {
    textAlign: 'center',
    marginBottom: Spacing.M,
  },
  progress: {
    flexDirection: 'row',
    marginBottom: Spacing.XS,
    alignItems: 'center',
  },
  bar: {
    flex: 1,
    marginRight: Spacing.S,
  },
})

interface Props {
  onCompleted?: () => void
  onFailed?: () => void
}

const FirmwareUpdate = (props: Props) => {
  const { onCompleted, onFailed } = props
  const { firmware } = useStore()
  const { t } = useTranslation()
  const [{ file }] = useParams()

  useKeepAwake()

  useEffect(() => {
    const path = Platform.select({
      ios: file,
      android: file?.replace('file://', ''),
    })
    firmware.start(path!)
  }, [])

  useEffect(() => {
    if (firmware.isFailed) {
      onFailed?.()
    } else if (firmware.isCompleted) {
      onCompleted?.()
    }
  }, [firmware.isCompleted, firmware.isFailed])

  function titleForState() {
    switch (firmware.state) {
      case FirmwareState.STARTING:
        return t('Starting test')
      case FirmwareState.SCANNING:
        return t('Looking for chargers')
      case FirmwareState.PREPARING:
        return t('Preparing device for firmware update')
      case FirmwareState.PROCESSING:
        return t('Starting firmware update')
      case FirmwareState.UPLOADING:
        return t('Uploading firmware')
      case FirmwareState.UPDATING:
        return t('Updating firmware')
    }
  }

  function infoForState() {
    switch (firmware.state) {
      case FirmwareState.STARTING:
      case FirmwareState.SCANNING:
      case FirmwareState.PREPARING:
        return t("Don't place your phone on the device")
      case FirmwareState.UPDATING:
        return t("Don't switch of the device! It will restart automatically after the update is complete.")
    }
  }

  function renderProgress() {
    const remainingMinutes = Math.floor((firmware.progress?.remaining || 0) / 1000 / 60)
    const remainingSeconds = Math.round((firmware.progress?.remaining || 0) / 1000 - 60 * remainingMinutes)
    const remaining = firmware.progress?.remaining
      ? `${formatDuration({ minutes: remainingMinutes, seconds: remainingSeconds })} remaining`
      : ''
    return (
      <View>
        <View style={styles.progress}>
          <ProgressBar style={styles.bar} value={firmware.progress?.value} />
          <Text level={7}>{(firmware.progress?.speed || 0).toFixed(1)} kb/s</Text>
        </View>
        <Text style={styles.text} level={7}>
          {remaining}
        </Text>
      </View>
    )
  }

  const title = titleForState()
  const info = infoForState()
  return (
    <View style={styles.container}>
      <Text style={styles.text}>{title}</Text>
      {info && <Text style={styles.text}>{info}</Text>}
      {firmware.state === FirmwareState.UPLOADING && renderProgress()}
    </View>
  )
}

export default observer(FirmwareUpdate)
